import { calculateWidgetGrid } from "@/features/workspace/calculateGrid";
import { useKeyListener } from "@/hooks/useKeyListener";
import {
  DndContext,
  type DragEndEvent,
  PointerSensor,
  useDraggable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToParentElement } from "@dnd-kit/modifiers";
import styled from "@emotion/styled";
import { type InstalledWidget, type Widget, type WidgetData } from "@repo/api";
import { Variables } from "@repo/skapa";
import { useRef, useState } from "react";

import { isInstalledWidget } from "../widgets/widgetSettings/widgetsSettings";
import { InstalledWidgetBox } from "./InstalledWidgetBox";

const getLastRow = (acc: number, widget: WidgetData) => {
  const maxHeight =
    widget.widgetSettings.top + widget.widgetSettings.appSize.row;
  if (maxHeight > acc) {
    return maxHeight;
  }

  return acc;
};

const Grid = styled.div<{ rows: number }>`
  display: flex;
  flex-direction: column;
  gap: ${Variables.space75};

  @media (min-width: ${Variables.breakpointL}) {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-template-rows: repeat(${({ rows }) => rows}, minmax(250px, 1fr));
  }
`;

interface GridCellProps {
  col?: string;
  row?: string;
}
const GridCell = styled.div<GridCellProps>`
  grid-column: ${(props) => props.col};
  grid-row: ${(props) => props.row};
`;

const DraggableGridCell = ({
  id,
  col,
  row,
  children,
}: {
  id: number;
  col?: string;
  row?: string;
  children: React.ReactNode;
}) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({ id });

  return (
    <GridCell
      ref={setNodeRef}
      col={col}
      row={row}
      style={{
        transform: transform
          ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
          : undefined,
        opacity: isDragging ? 0.5 : 1,
        zIndex: isDragging ? 1000 : undefined,
        position: "relative",
      }}
      {...listeners}
      {...attributes}
    >
      {children}
    </GridCell>
  );
};

interface InstalledWidgetsProps {
  installedWidgets: WidgetData[];
  allWidgets: Widget[];
  selectedWidget?: Widget | InstalledWidget;
  setSelectedWidgetId: (widgetId: number | undefined) => void;
  onReorderWidget: (
    id: number,
    direction: "LEFT" | "RIGHT" | "UP" | "DOWN"
  ) => Promise<void>;
  onMoveWidgetToPosition: (
    widgetId: number,
    newLeft: number,
    newTop: number
  ) => Promise<void>;
  onRemoveWidget: (widgetId: number) => Promise<void>;
}
export const InstalledWidgets = ({
  installedWidgets,
  allWidgets,
  selectedWidget,
  setSelectedWidgetId,
  onReorderWidget,
  onMoveWidgetToPosition,
  onRemoveWidget,
}: InstalledWidgetsProps) => {
  const grid = calculateWidgetGrid(installedWidgets);
  const rows = grid.reduce(getLastRow, 0);
  const [isMovingId, setIsMovingId] = useState<number | undefined>(undefined);
  const gridRef = useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, delta } = event;

    if (!gridRef.current || (delta.x === 0 && delta.y === 0)) {
      return;
    }

    const gridRect = gridRef.current.getBoundingClientRect();
    const cellWidth = gridRect.width / 4;
    const cellHeight = rows > 0 ? gridRect.height / rows : 250;

    const widgetId = active.id as number;
    const widget = installedWidgets.find(
      (w) => w.workspacePreferenceId === widgetId
    );
    if (!widget) {
      return;
    }

    const deltaCol = Math.round(delta.x / cellWidth);
    const deltaRow = Math.round(delta.y / cellHeight);

    if (deltaCol === 0 && deltaRow === 0) {
      return;
    }

    const newLeft = widget.widgetSettings.left + deltaCol;
    const newTop = widget.widgetSettings.top + deltaRow;

    onMoveWidgetToPosition(widgetId, newLeft, newTop);
  };

  // Listen to key events and move widgets accordingly

  // TODO: revisit the rule and fix error

  useKeyListener(async (e: any) => {
    if (isMovingId === undefined) {
      return;
    }
    const direction = getDirection(e.key);
    if (direction === "ESCAPE") {
      setIsMovingId(undefined);
      return;
    }
    if (direction) {
      await onReorderWidget(isMovingId, direction);
    }
  });

  return (
    <DndContext
      sensors={sensors}
      modifiers={[restrictToParentElement]}
      onDragEnd={handleDragEnd}
    >
      <Grid rows={rows} ref={gridRef}>
        {grid.map((widget) => {
          const w = allWidgets.find((i) => i.widgetType === widget.widgetType);
          if (!w) {
            return null;
          }

          return (
            <DraggableGridCell
              key={widget.workspacePreferenceId}
              id={widget.workspacePreferenceId}
              col={widget.gridCol}
              row={widget.gridRow}
            >
              <InstalledWidgetBox
                widgetDefinition={w}
                widgetData={widget}
                isMoving={isMovingId === widget.workspacePreferenceId}
                isSelected={
                  isInstalledWidget(selectedWidget) &&
                  selectedWidget.widgetType === widget.widgetType &&
                  selectedWidget.workspacePreferenceId ===
                    widget.workspacePreferenceId
                }
                onSetMoving={setIsMovingId}
                onRemoveWidget={onRemoveWidget}
                onReorderWidget={onReorderWidget}
                onClick={() => {
                  setIsMovingId((state) => {
                    return state !== widget.workspacePreferenceId
                      ? undefined
                      : state;
                  });
                  setSelectedWidgetId(
                    selectedWidget &&
                      isInstalledWidget(selectedWidget) &&
                      selectedWidget.workspacePreferenceId ===
                        widget.workspacePreferenceId
                      ? undefined
                      : widget.workspacePreferenceId
                  );
                }}
              />
            </DraggableGridCell>
          );
        })}
      </Grid>
    </DndContext>
  );
};

function getDirection(key: string) {
  switch (key) {
    case "ArrowUp":
      return "UP";
    case "ArrowDown":
      return "DOWN";
    case "ArrowLeft":
      return "LEFT";
    case "ArrowRight":
      return "RIGHT";
    case "Escape":
      return "ESCAPE";
  }

  return undefined;
}
